/**
 * manualIngestJobsStore — os manuais grandes que o motor lê em SEGUNDO PLANO.
 *
 * O motor lista o job do manual na mesma rota dos trabalhos de pesquisa
 * (`GET /api/v1/research/jobs/active`, `kind: 'manual_ingest'`). Mas ele NÃO é
 * pesquisa na web: se caísse no `researchJobsStore`, o aviso flutuante e o
 * painel de espera diriam "buscando plano do fabricante" para quem está lendo
 * um PDF anexado. Por isso o `hydrate` do `researchJobsStore` separa as linhas
 * e entrega estas aqui; os dois stores nunca se misturam.
 *
 * O motor não publica andamento por socket para o manual: a verdade é a lista
 * REST, e ela SUBSTITUI o estado inteiro (job que saiu da lista saiu do ar).
 */
import { manualIngestRun, type ManualIngestRun } from '@rochadenis/axiomgo-contracts'
import { create } from 'zustand'
import type {
  ManualDeletionRow,
  ManualIngestActiveRow,
  ManualIngestStage,
} from '../types/research-jobs'

export interface ManualIngestJobSnapshot {
  jobId: string
  conversationId: string
  /** Nome do arquivo anexado. */
  fileName: string
  startedAt: number
  stage: ManualIngestStage
  pagesTotal: number
  pagesDone: number
  pagesOcr: number
  pagesIllegible: number
  /** M3: quantas leituras estão à frente (0 = o próximo). Só em `queued`. */
  queuePosition?: number
  /** M6: a rodada de vários manuais (uma linha por arquivo). Ausente = manual avulso. */
  run?: ManualIngestRun
  /** O arquivo no motor, para a ação "Excluir manual". */
  fileId?: string
}

interface ManualIngestJobsStore {
  jobs: Record<string, ManualIngestJobSnapshot>
  /** Este usuário é Owner/Admin? O motor responde (`canDeleteManual`); sem resposta, NÃO (falha fechada). */
  canDelete: boolean
  /** Manuais excluídos há pouco, por arquivo: a ficha somente leitura. */
  deleted: Record<string, ManualDeletionRow>
  /** Troca o estado pela lista que o servidor acabou de devolver. */
  replaceFromServer: (
    rows: ManualIngestActiveRow[],
    extras?: { canDelete?: boolean; deletions?: ManualDeletionRow[] },
  ) => void
  /** A exclusão acabou de dar certo: a ficha vira somente leitura já, sem esperar a próxima leitura da lista. */
  markDeleted: (row: ManualDeletionRow) => void
  /** O usuário confirmou o aviso ("Vi o aviso"): o cartão sai, o motor já gravou o reconhecimento. */
  dismiss: (jobId: string) => void
}

export const MANUAL_RUNNING_STAGES: readonly ManualIngestStage[] = [
  'queued',
  'reading',
  'ocr',
  'indexing',
  'structuring',
]

/** Estados em que o cartão fica na conversa até o usuário tocar "Vi o aviso". */
export function isManualSettledWithNotice(stage: ManualIngestStage): boolean {
  return stage === 'failed' || stage === 'failed_retryable' || stage === 'cancelled'
}

/** Só o que o motor deixa retomar: falha retomável (`retry` da máquina de estado). */
export function canRetryManual(stage: ManualIngestStage): boolean {
  return stage === 'failed_retryable'
}

/** Trava de segurança do mês: o cartão fica até o usuário tocar Continuar ou Parar. */
export function isManualPaused(stage: ManualIngestStage): boolean {
  return stage === 'paused_decision'
}

/** O arquivo está na fila ou lendo: é o que o "Cancelar" do motor aceita. */
export function isManualRunning(stage: ManualIngestStage): boolean {
  return MANUAL_RUNNING_STAGES.includes(stage)
}

/** Manual lido e fora de rodada: a ficha só existe para quem pode excluir (QA99: não oferecer ação sem permissão do papel). */
export function isManualDeletable(job: ManualIngestJobSnapshot, canDelete: boolean): boolean {
  return canDelete && job.stage === 'ready' && job.run === undefined && job.fileId !== undefined
}

export const useManualIngestJobsStore = create<ManualIngestJobsStore>((set) => ({
  jobs: {},
  canDelete: false,
  deleted: {},
  markDeleted: (row) => set((state) => ({ deleted: { ...state.deleted, [row.fileId]: row } })),
  replaceFromServer: (rows, extras) => {
    const jobs: Record<string, ManualIngestJobSnapshot> = {}
    for (const row of rows) {
      // Contrato 0.37.0: rodada malformada vira cartão avulso, nunca derruba a lista.
      const parsed = row.manualIngestRun ? manualIngestRun.safeParse(row.manualIngestRun) : null
      const run = parsed?.success ? (parsed.data as ManualIngestRun) : undefined
      jobs[row.jobId] = {
        jobId: row.jobId,
        conversationId: row.conversationId,
        fileName: row.label,
        startedAt: Date.parse(row.startedAt),
        stage: row.progress.stage,
        pagesTotal: row.progress.pagesTotal,
        pagesDone: row.progress.pagesDone,
        pagesOcr: row.progress.pagesOcr,
        pagesIllegible: row.progress.pagesIllegible,
        ...(row.queuePosition === undefined ? {} : { queuePosition: row.queuePosition }),
        ...(run ? { run } : {}),
        ...(row.fileId ? { fileId: row.fileId } : {}),
      }
    }
    set((state) => ({
      jobs,
      canDelete: extras?.canDelete === true,
      // A lista do motor é a verdade e vale sobre o que acabou de ser marcado aqui (traz o nome de quem excluiu).
      deleted: {
        ...state.deleted,
        ...Object.fromEntries((extras?.deletions ?? []).map((d) => [d.fileId, d])),
      },
    }))
  },
  dismiss: (jobId) =>
    set((state) => {
      if (!(jobId in state.jobs)) return state
      const jobs = { ...state.jobs }
      delete jobs[jobId]
      return { jobs }
    }),
}))
